import { forwardRef, ReactNode, useContext } from "react";

import { Stack, StackProps, useTheme } from "@mui/material";
import { isQuebecOrganization, OrganizationCountry } from "@simplyk/common";

import QuebecFlag from "./assets/quebec-flag.svg";

import { FrontendFormContext } from "@/contexts/FrontendFormContext";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { Trans } from "@/hooks/useTranslate";

type FormPaymentTipSectionProps = Omit<StackProps, "children"> & {
  organizationName: string;
  helperText?: ReactNode;
  organizationCountry: OrganizationCountry;
  slots: {
    learnMore?: ReactNode;
    tipSelect: ReactNode;
    voluntaryContribution?: ReactNode;
    tipAmountConfirmation?: ReactNode;
  };
};

export const FormPaymentTipSection = forwardRef<HTMLDivElement, FormPaymentTipSectionProps>(
  ({ organizationName, organizationCountry, helperText, slots, ...rest }, ref) => {
    const theme = useTheme();
    const { organization } = useContext(FrontendFormContext);

    if (!theme.palette.forms || !theme.palette.formsComponents?.paymentSummary) {
      return <InvalidThemeErrorMessage />;
    }

    return (
      <Stack ref={ref} gap={1} alignItems="flex-start" data-test="tip-section" {...rest}>
        <FormTypography variant="subtitle1" vibe="moderate">
          <Trans
            space="common"
            i18nKey="paymentSummary.tipTitle"
            values={{ organizationName }}
            components={{
              subtle: <FormTypography vibe="supershy" component="span" />,
            }}
          />
        </FormTypography>
        <FormTypography variant="body2" vibe="moderate">
          <Trans
            space="common"
            i18nKey={
              organizationCountry === OrganizationCountry.Canada
                ? "paymentSummary.tipDescriptionCanadaNewExperiment"
                : "paymentSummary.tipDescription"
            }
            values={{ organizationName }}
            components={{
              sb: <FormTypography variant="subtitle2" component="span" />,
            }}
          />
        </FormTypography>
        {organizationCountry === OrganizationCountry.Canada && (
          <FormTypography variant="body2" vibe="moderate" marginTop={-0.7}>
            <Trans
              space="common"
              i18nKey={
                isQuebecOrganization(organization?.region)
                  ? "paymentSummary.tipDescriptionCanadaNewExperiment.proudlyQuebec"
                  : "paymentSummary.tipDescriptionCanadaNewExperiment.proudlyCanadian"
              }
              values={{ organizationName }}
              components={{
                sb: <FormTypography variant="subtitle2" component="span" />,
                flag: isQuebecOrganization(organization?.region) ? (
                  <QuebecFlag
                    height={14}
                    style={{
                      position: "relative",
                      top: 2,
                      display: "inline-block",
                      marginInlineStart: 2,
                      marginRight: 2,
                    }}
                  />
                ) : (
                  <span style={{ marginRight: 2 }}>🇨🇦</span>
                ),
                italic: <em style={{ fontStyle: "italic" }} />,
              }}
            />
          </FormTypography>
        )}
        {slots.learnMore}
        <Stack
          width="100%"
          maxWidth="180px"
          gap={0.5}
          marginBlockStart={slots.learnMore ? "-28px" : undefined}
          sx={(theme) => ({
            alignSelf: "flex-end",
            [theme.breakpoints.down(360)]: {
              marginBlockStart: slots.learnMore ? "initial" : undefined,
            },
          })}
        >
          <Stack gap={2}>
            {slots.tipSelect}
            {slots.voluntaryContribution}
          </Stack>
          {helperText && (
            <FormTypography variant="caption" vibe="brand">
              {helperText}
            </FormTypography>
          )}
        </Stack>
        {slots.tipAmountConfirmation}
      </Stack>
    );
  }
);

FormPaymentTipSection.displayName = "FormPaymentTipSection";
